import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import ResponsiveIframe from "components/ResponsiveIframe";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import contextStore from "stores/contextStore";
import translateError from "utils/translateError";

const styles = {
  titleColumn: { width: "200px", fontWeight: "bold" },
};

const EmailThemeDetails = () => {
  const navigate = useNavigate();
  const { formatMessage: t } = useIntl();
  const {
    emailThemeCreateUrl,
    emailThemeEditUrl,
    notificationsUrl,
    notificationService,
  } = useGlobalContext();
  const { organizationId, organizationName } = contextStore.useState();

  const [deleteDialogOpen, deleteDialogOpenActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(
    ["getEmailTheme", organizationId],
    () => notificationService.getEmailTheme()
  );

  useTitle(t({ id: "emailThemeTitle" }, { organizationName }));

  if (isLoading) return <Loader />;
  if (isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="fetchError" />
        </Alert>
      </Box>
    );
  }

  const emailTheme = data?.data;

  const handleDelete = async () => {
    try {
      const response = await registerPromise(
        notificationService.deleteEmailTheme(emailTheme?.settingValueId!)
      );
      queryClient.invalidateQueries("settingById");
      queryClient.invalidateQueries("settings");
      queryClient.invalidateQueries("getEmailTheme");

      deleteDialogOpenActions.setFalse();

      return response;
    } catch (error) {
      return { [FORM_ERROR]: translateError(error, t) };
    }
  };

  const isInherited =
    emailTheme?.inheritedOrganizationName &&
    emailTheme.inheritedOrganizationName !== organizationName;

  const themeValue = emailTheme?.value;

  const rows = [
    {
      title: t({ id: "settingInheritedFromLabel" }),
      component: themeValue && isInherited && (
        <span>{emailTheme.inheritedOrganizationName}</span>
      ),
    },
    {
      title: t({ id: "preview" }),
      component: themeValue && (
        <ResponsiveIframe srcDoc={themeValue} title="emailThemePreview" />
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={notificationsUrl()}
        labelKey="backToNotifications"
      />
      <h2>
        <T id="emailTheme" />: {organizationName}
      </h2>

      <Box my={3} display="flex" justifyContent="center">
        {themeValue && !isInherited ? (
          <>
            <Link to={emailThemeEditUrl()}>
              <Fab color="primary">
                <EditIcon />
              </Fab>
            </Link>
            <Box ml={4} />
            <Fab color="secondary" onClick={deleteDialogOpenActions.setTrue}>
              <DeleteIcon />
            </Fab>
          </>
        ) : (
          <Fab
            color="primary"
            onClick={() => navigate(emailThemeCreateUrl())}
          >
            <AddIcon />
          </Fab>
        )}
      </Box>

      {!themeValue && (
        <Alert severity="info">
          <T id="noEmailTheme" />
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }) => (
              <TableRow key={title}>
                <TableCell sx={styles.titleColumn}>{title}</TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteEntityDialog
        dialogTitleKey="areYouSure"
        rawContent={t(
          {
            id: "deleteEmailThemeConfirmation",
          },
          { organizationName }
        )}
        onCancel={deleteDialogOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteDialogOpen}
      />
    </>
  );
};

export default EmailThemeDetails;
